import { flushSync } from "react-dom";

const THROTTLE_TIME = 50;

type MeasureRequest = {
  path: HTMLElement;
  onWidth: (width: number) => void;
  cancelled: boolean;
};

let pending: MeasureRequest[] = [];
let scheduled = false;
let lastFlushAt = -Infinity;

const flushAvailableWidthReads = () => {
  scheduled = false;

  const requests = pending;
  pending = [];

  const active = requests.filter((request) => !request.cancelled);
  if (active.length === 0) {
    return;
  }

  lastFlushAt = Date.now();

  const previousWidths = active.map(({ path }) => path.style.width);

  // Measuring the path as-is reads back its own truncated width, ratcheting down to the leaf
  // and never recovering. Claiming the viewport first reads the real constraint instead — as
  // long as an ancestor has a definite width; a shrink-to-fit one just grows to the 100vw.
  for (const { path } of active) {
    path.style.width = "100vw";
  }

  const widths = active.map(({ path }) => path.getBoundingClientRect().width);

  active.forEach(({ path }, index) => {
    path.style.width = previousWidths[index];
  });

  // Keep the truncated first paint: these setStates are scheduled from a microtask, which
  // React would otherwise render after the browser has painted the full path.
  flushSync(() => {
    active.forEach(({ onWidth }, index) => {
      onWidth(widths[index]);
    });
  });
};

/**
 * Leading-edge throttle shared by every instance — per-instance timers would fire one by one
 * and each drain the queue alone. The leading edge only queues a microtask, so everything
 * scheduled within one task (mount, or one resize delivery) still lands in a single flush.
 */
const scheduleFlush = () => {
  if (scheduled) {
    return;
  }

  scheduled = true;

  // Clamped: a system clock stepping backwards would otherwise push the timer out by the size
  // of the jump, and `scheduled` blocks every other instance until it fires.
  const wait = Math.min(THROTTLE_TIME, THROTTLE_TIME - (Date.now() - lastFlushAt));

  if (wait <= 0) {
    queueMicrotask(flushAvailableWidthReads);
  } else {
    setTimeout(flushAvailableWidthReads, wait);
  }
};

/** Coalesce every instance's write→read→write into one reflow instead of one per instance. */
export const scheduleSpacePathAvailableWidthMeasure = (
  path: HTMLElement,
  onWidth: (width: number) => void
) => {
  for (const request of pending) {
    if (request.path === path) {
      request.cancelled = true;
    }
  }

  const request: MeasureRequest = { path, onWidth, cancelled: false };
  pending.push(request);
  scheduleFlush();

  return () => {
    request.cancelled = true;
  };
};
